import { useEffect, useRef, useState } from 'preact/hooks';
import jsQR from '../../vendor/jsqr/jsQR.cjs';
import { tryPaymentRequest } from '../core';
import { PlainButton, Sheet, Spinner, haptic } from './components';
import { Camera } from './icons';
import { ui, useApp } from './state';

type Access = 'checking' | 'granted' | 'denied' | 'unavailable';

/** Live camera preview that reports the first QR code it reads. */
export function Scanner({ onCode }: { onCode: (code: string) => void }) {
  const { s } = useApp();
  const video = useRef<HTMLVideoElement>(null);
  const [access, setAccess] = useState<Access>('checking');

  useEffect(() => {
    let stream: MediaStream | null = null;
    let stopped = false;
    let timer = 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const scan = () => {
      const v = video.current;
      if (stopped || !v || !ctx) return;
      if (v.readyState >= 2 && v.videoWidth > 0) {
        const w = Math.min(640, v.videoWidth);
        const h = Math.round((v.videoHeight * w) / v.videoWidth);
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(v, 0, 0, w, h);
        const img = ctx.getImageData(0, 0, w, h);
        const found = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' });
        if (found?.data) {
          stopped = true;
          haptic();
          onCode(found.data);
          return;
        }
      }
      timer = window.setTimeout(scan, 200);
    };

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) return setAccess('unavailable');
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (stopped) return stream.getTracks().forEach((t) => t.stop());
        setAccess('granted');
        const v = video.current!;
        v.srcObject = stream;
        await v.play().catch(() => {});
        scan();
      } catch (e: any) {
        setAccess(e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError' ? 'unavailable' : 'denied');
      }
    })();

    return () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div class="scanner">
      <video ref={video} playsInline muted class={access === 'granted' ? '' : 'hidden'} />
      {access === 'checking' && <Spinner />}
      {access === 'granted' && (
        <>
          <div class="viewfinder" />
          <div class="scan-hint glass-pill">{s.scanHint}</div>
        </>
      )}
      {(access === 'denied' || access === 'unavailable') && (
        <div class="scan-message">
          <Camera size={40} />
          <p>{access === 'denied' ? s.scanCameraDenied : s.scanNoCamera}</p>
        </div>
      )}
    </div>
  );
}

/** "Scan" tab: a valid COINWOW code opens the send screen pre-filled. */
export function ScanTab() {
  const { s } = useApp();
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  if (ui.tab !== 'scan' || ui.sheets.length > 0) return <div class="tab-page scan-page" />;
  return (
    <div class="scan-page">
      <Scanner key={attempt} onCode={(code) => {
        if (tryPaymentRequest(code)) {
          setError(null);
          ui.setTab('wallet');
          ui.open({ type: 'send', prefill: code });
        } else {
          setError(s.scanNotCoinwow);
          setTimeout(() => setAttempt((a) => a + 1), 1500);
        }
      }} />
      {error && <div class="scan-error glass-pill">{error}</div>}
    </div>
  );
}

export function ScannerSheet({ onCode }: { onCode: (code: string) => void }) {
  const { s } = useApp();
  return (
    <Sheet title={s.scanTitle} left={<PlainButton onClick={() => ui.close()}>{s.cancel}</PlainButton>} class="dark-sheet">
      <Scanner onCode={(c) => { ui.close(); onCode(c); }} />
    </Sheet>
  );
}
